// src/hooks/useBulkActions.js
import { useState, useCallback } from 'react';

/**
 * Custom hook for managing bulk actions on biodata
 * @returns {Object} Bulk action state and handlers
 */
export const useBulkActions = () => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Selection handlers
  const selectItem = useCallback((itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  }, []);

  const selectAll = useCallback((items) => {
    if (selectedItems.length === items.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(items.map(item => item.id));
    }
  }, [selectedItems.length]);

  const clearSelection = useCallback(() => {
    setSelectedItems([]);
    setShowBulkActions(false);
  }, []);

  const isSelected = useCallback((itemId) => {
    return selectedItems.includes(itemId);
  }, [selectedItems]);

  const isAllSelected = useCallback((items) => {
    return items.length > 0 && selectedItems.length === items.length;
  }, [selectedItems]);

  const hasSelection = useCallback(() => {
    return selectedItems.length > 0;
  }, [selectedItems]);

  // Bulk action executors
  const executeBulkAction = useCallback(async (actionFn, successMessage, errorMessage) => {
    if (selectedItems.length === 0) {
      return { success: false, message: 'No items selected' };
    }

    try {
      setBulkLoading(true);
      const result = await actionFn(selectedItems);
      
      if (result.success) {
        clearSelection();
        return { 
          success: true, 
          message: successMessage || `${selectedItems.length} items processed successfully` 
        };
      } else {
        return { 
          success: false, 
          message: result.message || errorMessage || 'Bulk action failed' 
        };
      }
    } catch (error) {
      console.error('Bulk action error:', error);
      return { 
        success: false, 
        message: errorMessage || 'An error occurred during bulk action' 
      };
    } finally {
      setBulkLoading(false);
    }
  }, [selectedItems, clearSelection]);

  // Specific bulk actions
  const bulkApprove = useCallback(async (approveFn) => {
    return executeBulkAction(
      approveFn,
      `${selectedItems.length} biodata approved successfully`,
      'Failed to approve selected biodata'
    );
  }, [executeBulkAction, selectedItems.length]);

  const bulkReject = useCallback(async (rejectFn) => {
    return executeBulkAction(
      rejectFn,
      `${selectedItems.length} biodata rejected successfully`,
      'Failed to reject selected biodata'
    );
  }, [executeBulkAction, selectedItems.length]);

  const bulkGrantPermission = useCallback(async (grantPermissionFn) => {
    return executeBulkAction(
      grantPermissionFn,
      `Edit permission granted to ${selectedItems.length} biodata successfully`,
      'Failed to grant edit permissions'
    );
  }, [executeBulkAction, selectedItems.length]);

  const bulkDelete = useCallback(async (deleteFn) => {
    return executeBulkAction(
      deleteFn,
      `${selectedItems.length} items deleted successfully`,
      'Failed to delete selected items'
    );
  }, [executeBulkAction, selectedItems.length]);

  // Bulk export/print actions
  const bulkExport = useCallback(async (exportFn, format = 'excel') => {
    if (selectedItems.length === 0) {
      return { success: false, message: 'No items selected for export' };
    }

    try {
      setBulkLoading(true);
      await exportFn(selectedItems, format);
      return { 
        success: true, 
        message: `${selectedItems.length} items exported successfully` 
      };
    } catch (error) {
      console.error('Bulk export error:', error);
      return { 
        success: false, 
        message: 'Failed to export selected items' 
      };
    } finally {
      setBulkLoading(false);
    }
  }, [selectedItems]);

  const bulkPrint = useCallback((printFn) => {
    if (selectedItems.length === 0) {
      return { success: false, message: 'No items selected for printing' };
    }

    try {
      printFn(selectedItems);
      return { 
        success: true, 
        message: `${selectedItems.length} items sent to printer` 
      };
    } catch (error) {
      console.error('Bulk print error:', error);
      return { 
        success: false, 
        message: 'Failed to print selected items' 
      };
    }
  }, [selectedItems]);

  // Toggle bulk actions panel
  const toggleBulkActions = useCallback(() => {
    setShowBulkActions(prev => !prev);
  }, []);

  return {
    // State
    selectedItems,
    bulkLoading,
    showBulkActions,
    
    // Selection handlers
    selectItem,
    selectAll,
    clearSelection,
    isSelected,
    isAllSelected,
    hasSelection,
    
    // Bulk actions
    executeBulkAction,
    bulkApprove,
    bulkReject,
    bulkGrantPermission,
    bulkDelete,
    bulkExport,
    bulkPrint,
    
    // UI handlers
    toggleBulkActions,
    setShowBulkActions,
    
    // Utilities
    selectedCount: selectedItems.length
  };
};